import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';

const ClientDashboard = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);

  // Get API URL from environment variables - use relative path in production
  const API_URL = process.env.NODE_ENV === 'production' ? '/api' : (process.env.REACT_APP_API_URL || 'http://localhost:5001/api');

  useEffect(() => {
    const fetchImages = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/images`);
        setImages(response.data.images || []);
      } catch (err) {
        console.error('Error fetching images:', err);
        // Instead of showing an error, just set empty images array
        setImages([]);
      } finally {
        setLoading(false);
      }
    };

    fetchImages();
  }, [API_URL]);
  
  const handleDownloadImage = async (id) => {
    try {
      // Get the authentication token from localStorage
      const token = localStorage.getItem('token');
      
      // Make an authenticated request to get the download URL
      const response = await axios.get(`${API_URL}/images/${id}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Open the download URL in a new tab
      if (response.data && response.data.url) {
        window.open(response.data.url, '_blank');
      } else {
        alert('Download link not available');
      }
    } catch (err) {
      console.error('Error downloading image:', err);
      alert('Failed to download image. Please try again.');
    }
  };

  if (loading) {
    return (
      <LoadingContainer>
        <div className="loading-spinner"></div>
        <p>Loading images...</p>
      </LoadingContainer>
    );
  }

  return (
    <DashboardContainer>
      <DashboardHeader>
        <h1>Verizon QCOW Image Portal</h1>
      </DashboardHeader>



      <SectionTitle>Available Images</SectionTitle>
      
      {images.length === 0 ? (
        <EmptyState>
          <p>No images are currently available for download.</p>
          <p>Please contact your administrator if you believe this is an error.</p>
        </EmptyState>
      ) : (
        <ImageGrid>
          {images.map(image => (
            <ImageCard key={image.id}>
              <ImageIcon>
                <i className="fas fa-file-image"></i>
              </ImageIcon>
              <ImageInfo>
                <ImageName>{image.name}</ImageName>
                <ImageVersion>Version: {image.version}</ImageVersion>
                <ImageSize>{(image.size / (1024 * 1024 * 1024)).toFixed(2)} GB</ImageSize>
                <ImageDate>Uploaded: {new Date(image.uploadedAt).toLocaleDateString()}</ImageDate>
                {image.description && <ImageDescription>{image.description}</ImageDescription>}
              </ImageInfo>
              <DownloadButton onClick={() => handleDownloadImage(image.id)}>
                Download
              </DownloadButton>
            </ImageCard>
          ))}
        </ImageGrid>
      )}
    </DashboardContainer>
  );
};

// Styled components
const DashboardContainer = styled.div`
  padding: ${props => props.theme.spacing.lg};
`;

const DashboardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const SectionTitle = styled.h2`
  margin-bottom: ${props => props.theme.spacing.lg};
  border-bottom: 2px solid ${props => props.theme.colors.border};
  padding-bottom: ${props => props.theme.spacing.sm};
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: ${props => props.theme.spacing.lg};
`;

const ImageCard = styled.div`
  background-color: white;
  border-radius: ${props => props.theme.borderRadius.medium};
  box-shadow: ${props => props.theme.shadows.small};
  padding: ${props => props.theme.spacing.lg};
  display: flex;
  flex-direction: column;
  transition: ${props => props.theme.transitions.short};

  &:hover {
    box-shadow: ${props => props.theme.shadows.medium};
    transform: translateY(-2px);
  }
`;

const ImageIcon = styled.div`
  font-size: 48px;
  color: ${props => props.theme.colors.opplaneBlue};
  margin-bottom: ${props => props.theme.spacing.md};
  text-align: center;
`;

const ImageInfo = styled.div`
  flex: 1;
`;

const ImageName = styled.h3`
  font-size: ${props => props.theme.fontSizes.large};
  margin-bottom: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.opplaneBlue};
`;

const ImageVersion = styled.div`
  font-size: ${props => props.theme.fontSizes.medium};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const ImageSize = styled.div`
  font-size: ${props => props.theme.fontSizes.medium};
  margin-bottom: ${props => props.theme.spacing.xs};
  color: ${props => props.theme.colors.lightText};
`;

const ImageDate = styled.div`
  font-size: ${props => props.theme.fontSizes.small};
  margin-bottom: ${props => props.theme.spacing.md};
  color: ${props => props.theme.colors.lightText};
`;

const ImageDescription = styled.p`
  font-size: ${props => props.theme.fontSizes.medium};
  margin-bottom: ${props => props.theme.spacing.md};
  color: ${props => props.theme.colors.text};
`;

const DownloadButton = styled.button`
  background-color: ${props => props.theme.colors.opplaneBlue};
  color: white;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: none;
  border-radius: ${props => props.theme.borderRadius.small};
  font-weight: bold;
  cursor: pointer;
  transition: ${props => props.theme.transitions.short};
  align-self: flex-end;

  &:hover {
    background-color: ${props => props.theme.colors.opplaneDarkBlue};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl};
  background-color: white;
  border-radius: ${props => props.theme.borderRadius.medium};
  box-shadow: ${props => props.theme.shadows.small};

  p {
    margin-bottom: ${props => props.theme.spacing.md};
    color: ${props => props.theme.colors.lightText};
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing.xl};

  .loading-spinner {
    border: 4px solid rgba(0, 0, 0, 0.1);
    border-radius: 50%;
    border-top: 4px solid ${props => props.theme.colors.opplaneBlue};
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
    margin-bottom: ${props => props.theme.spacing.md};
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export default ClientDashboard;
